import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { hasCapability } from '@/lib/capabilities';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ChevronLeft, Wallet, RefreshCw, RotateCcw } from 'lucide-react';
import PaymentDialog from '@/components/POS/PaymentDialog';
import RefundDialog from '@/components/POS/RefundDialog';

const PS_CLS = {
  PAID: 'text-green-600 bg-green-50',
  PENDING: 'text-amber-600 bg-amber-50',
  PARTIALLY_REFUNDED: 'text-orange-600 bg-orange-50',
  REFUNDED: 'text-slate-600 bg-slate-100',
  FAILED: 'text-red-600 bg-red-50',
};

export default function PaymentsRefunds() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const storeId = user?.store_id;
  const canPay = hasCapability(user, 'payments.record');

  const [orders, setOrders] = useState([]);
  const [payOrder, setPayOrder] = useState(null);
  const [refundOrder, setRefundOrder] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get(`/orders?store_id=${storeId}&limit=50`);
      setOrders(res.data || []);
    } catch { toast.error('Failed to load orders'); }
  }, [storeId]);

  useEffect(() => {
    if (!user?.id) { navigate('/login'); return; }
    if (!canPay) { toast.error('No permission'); navigate('/pos-dashboard'); return; }
    load();
    // eslint-disable-next-line
  }, [load]);

  const isPaid = (o) => ['PAID', 'PARTIALLY_REFUNDED', 'REFUNDED'].includes((o.payment_status || '').toUpperCase());

  return (
    <div className="min-h-screen bg-slate-50" data-testid="payments-refunds-page">
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pos-dashboard')} data-testid="pr-back"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-sspice" />
            <div>
              <h1 className="font-heading text-lg font-bold text-slate-900">Payments &amp; Refunds</h1>
              <p className="text-xs text-slate-500">Collect payment or issue a refund on any order</p>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="pr-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="max-w-3xl mx-auto p-4">
        {orders.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="pr-empty">No orders yet.</div>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl" data-testid={`pr-order-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">Order #{o.order_number} · {o.order_type}</p>
                  <p className="text-xs text-slate-400">₹{Number(o.total || 0).toFixed(2)} · {o.status}</p>
                  {(o.customer?.name || o.customer?.phone) && (
                    <p className="text-xs text-slate-500 mt-0.5" data-testid={`pr-customer-${o.id}`}>
                      {o.customer?.name || 'Guest'}{o.customer?.phone ? ` · ${o.customer.phone}` : ''}
                    </p>
                  )}
                  {o.items && o.items.length > 0 && (
                    <p className="text-xs text-slate-500 truncate mt-0.5" data-testid={`pr-items-${o.id}`}>
                      {o.items.map((it) => `${it.qty}× ${it.menu_item_name}`).join(', ')}
                    </p>
                  )}
                </div>
                <span className={`text-[11px] font-semibold px-2 py-1 rounded ${PS_CLS[(o.payment_status || 'PENDING').toUpperCase()] || 'bg-slate-100'}`} data-testid={`pr-paystatus-${o.id}`}>
                  {o.payment_status || 'PENDING'}
                </span>
                {!isPaid(o) && o.status !== 'CANCELLED' && (
                  <Button size="sm" className="bg-sspice hover:bg-sspice-hover" onClick={() => setPayOrder(o)} data-testid={`pr-collect-${o.id}`}>Collect Payment</Button>
                )}
                {isPaid(o) && (o.payment_status || '').toUpperCase() !== 'REFUNDED' && (
                  <Button size="sm" variant="outline" onClick={() => setRefundOrder(o)} data-testid={`pr-refund-${o.id}`}><RotateCcw className="w-4 h-4 mr-1" />Refund</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <PaymentDialog order={payOrder} open={!!payOrder} onClose={() => setPayOrder(null)}
        onPaid={() => { setPayOrder(null); load(); }} />
      <RefundDialog order={refundOrder} open={!!refundOrder} onClose={() => setRefundOrder(null)}
        onRefunded={load} />
    </div>
  );
}
